import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getProspectSubmissions,
  useApproveProspectSubmission,
  useRevokeProspectSubmission,
} from '../queries/prospectSubmissions';
import ProspectsShell from '../components/Prospects/ProspectsShell';
import ProspectsEmptyState from '../components/Prospects/ProspectsEmptyState';
import ClientProspectDetailsModal from '../components/Prospects/ClientProspectDetailsModal';
import ProspectDecisionDialog from '../components/Prospects/ProspectDecisionDialog';
import ProspectStatusChip from '../components/Prospects/ProspectStatusChip';
import { PROSPECT_SUBMISSION_STATUS } from '../constants';
import {
  formatSubmittedDate,
  leadAgentName,
  prospectErrorMessage,
  tableCellSx,
  tableHeadCellSx,
} from '../components/Prospects/prospectFields';

const SUBTITLE = 'Review and manage prospects submitted for your programs.';

const matchesSearch = (row, query) => {
  const submittedBy = leadAgentName(row.leadAgent);
  return [row.company, row.contactName, row.title, submittedBy].join('\n').toLowerCase().includes(query);
};

const ClientProspectsPage = () => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState(null);
  const { data: prospects, isLoading, isError } = useQuery(getProspectSubmissions());
  const { mutate: approveProspect, isPending: isApproving } = useApproveProspectSubmission();
  const { mutate: revokeProspect, isPending: isRevoking } = useRevokeProspectSubmission();

  const rows = prospects || [];
  const isEmpty = !isLoading && !isError && rows.length === 0;
  const query = search.trim().toLowerCase();
  const visibleRows = useMemo(() => (query ? rows.filter((row) => matchesSearch(row, query)) : rows), [rows, query]);
  const isBusy = isApproving || isRevoking;

  const confirmDecision = () => {
    if (!decision) return;
    const isApprove = decision.mode === 'approve';
    const action = isApprove ? approveProspect : revokeProspect;
    action(
      { id: decision.id },
      {
        onSuccess: () => {
          toast(isApprove ? 'Prospect approved.' : 'Prospect returned to pending.', { type: 'success' });
          setDecision(null);
        },
        onError: (error) => toast(prospectErrorMessage(error, 'Could not update this prospect.'), { type: 'error' }),
      },
    );
  };

  return (
    <ProspectsShell
      title='Prospects'
      subtitle={SUBTITLE}
      action={
        rows.length > 0 ? (
          <TextField
            size='small'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Search prospects...'
            sx={{ width: { xs: 180, sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />
        ) : null
      }
    >
      {isLoading ? (
        <Box display='flex' justifyContent='center' py={10}>
          <CircularProgress />
        </Box>
      ) : null}
      {isError ? (
        <Typography color='error' align='center'>
          Could not load prospects.
        </Typography>
      ) : null}
      {isEmpty ? (
        <ProspectsEmptyState
          title='No prospects yet'
          description='When Lead Agents submit prospects, they will appear here for your review.'
        />
      ) : null}
      {!isLoading && !isError && rows.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  'Company',
                  'Contact name',
                  'Title',
                  'Submitted by',
                  'Date submitted',
                  'Status',
                  'Details',
                  'History',
                  'Actions',
                ].map((heading) => (
                  <TableCell key={heading} sx={tableHeadCellSx}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length ? (
                visibleRows.map((row) => {
                  const pending = row.status === PROSPECT_SUBMISSION_STATUS.PENDING;
                  return (
                    <TableRow key={row._id} sx={{ '& > *': { py: 1.5 } }}>
                      <TableCell sx={tableCellSx}>{row.company}</TableCell>
                      <TableCell sx={tableCellSx}>{row.contactName}</TableCell>
                      <TableCell sx={tableCellSx}>{row.title || '—'}</TableCell>
                      <TableCell sx={tableCellSx}>{leadAgentName(row.leadAgent) || '—'}</TableCell>
                      <TableCell sx={tableCellSx}>{formatSubmittedDate(row.submittedAt)}</TableCell>
                      <TableCell sx={tableCellSx}>
                        <ProspectStatusChip status={row.status} />
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Link component='button' type='button' underline='hover' onClick={() => setSelected(row)}>
                          View
                        </Link>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Typography variant='body2' color='text.disabled' aria-disabled='true'>
                          Show
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        {pending ? (
                          <Tooltip title='Approve'>
                            <span>
                              <IconButton
                                aria-label='Approve'
                                disabled={isBusy}
                                onClick={() => setDecision({ mode: 'approve', id: row._id })}
                              >
                                <CheckCircleIcon color={isBusy ? 'disabled' : 'success'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title='Revoke'>
                            <span>
                              <IconButton
                                aria-label='Revoke'
                                disabled={isBusy}
                                onClick={() => setDecision({ mode: 'revoke', id: row._id })}
                              >
                                <CancelIcon color={isBusy ? 'disabled' : 'error'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align='center' sx={{ py: 6, color: 'text.secondary' }}>
                    No prospects match your search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
      <ClientProspectDetailsModal open={Boolean(selected)} prospect={selected} onClose={() => setSelected(null)} />
      <ProspectDecisionDialog
        open={Boolean(decision)}
        mode={decision?.mode}
        isSaving={isBusy}
        onCancel={() => setDecision(null)}
        onConfirm={confirmDecision}
      />
    </ProspectsShell>
  );
};

export default ClientProspectsPage;
